import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type { DecorationCategory, DecorationKind, SubmitClaimInput } from '@clan-heart/shared';
import { DECORATION_CATEGORIES } from '@clan-heart/shared';
import { claimAccomplishment, getAccomplishments, withdrawClaim } from '../api.js';
import { AccomplishmentCard } from '../components/AccomplishmentCard.js';
import { Button } from '../components/Button.js';
import type { AccomplishmentFilters, ClaimFilter } from '../lib/accomplishmentFilter.js';
import { filterAccomplishments, filtersFromParams, filtersToParams } from '../lib/accomplishmentFilter.js';
import styles from './AccomplishmentsPage.module.css';

const CLAIM_FILTERS: ReadonlyArray<{ value: ClaimFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'claimable', label: 'Can claim' },
  { value: 'pending', label: 'Pending' },
  { value: 'earned', label: 'Earned' },
];

const KIND_FILTERS: ReadonlyArray<{ value: DecorationKind | 'all'; label: string }> = [
  { value: 'all', label: 'All kinds' },
  { value: 'ribbon', label: 'Ribbons' },
  { value: 'medal', label: 'Medals' },
  { value: 'ensign', label: 'Ensigns' },
];

export function AccomplishmentsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['accomplishments'],
    queryFn: () => getAccomplishments(),
  });
  // filters live in the URL, so a refresh or Back keeps them
  const [params, setParams] = useSearchParams();
  const filters = filtersFromParams(params);

  function setFilters(next: Partial<AccomplishmentFilters>) {
    setParams(filtersToParams({ ...filters, ...next }), { replace: true });
  }

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['accomplishments'] });
    queryClient.invalidateQueries({ queryKey: ['claims'] });
  }

  const claimMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: SubmitClaimInput }) =>
      claimAccomplishment(id, input),
    onSuccess: invalidate,
  });

  const withdrawMutation = useMutation({
    mutationFn: (claimId: string) => withdrawClaim(claimId),
    onSuccess: invalidate,
  });

  const accomplishments = query.data?.accomplishments ?? [];
  const shown = filterAccomplishments(accomplishments, filters);
  const filtering = filtersToParams(filters).toString() !== '';

  if (query.isLoading) return <p>Loading…</p>;
  if (query.isError) return <p className={styles.empty}>{(query.error as Error).message}</p>;

  return (
    <section>
      <h2 className={styles.heading}>Accomplishments</h2>
      {accomplishments.length === 0 && (
        <p className={styles.empty}>Nothing to claim yet — check back soon.</p>
      )}

      {accomplishments.length > 0 && (
        <div className={styles.filters}>
          <input
            type="search"
            className={styles.search}
            placeholder="Search accomplishments…"
            value={filters.search}
            onChange={(e) => setFilters({ search: e.target.value })}
            aria-label="Search accomplishments"
          />
          <div className={styles.filterRow}>
            <div className={styles.segments} role="group" aria-label="Show">
              {CLAIM_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  aria-pressed={filters.claim === f.value}
                  className={filters.claim === f.value ? `${styles.segment} ${styles.segmentOn}` : styles.segment}
                  onClick={() => setFilters({ claim: f.value })}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <select
              className={styles.select}
              value={filters.kind}
              onChange={(e) => setFilters({ kind: e.target.value as AccomplishmentFilters['kind'] })}
              aria-label="Kind"
            >
              {KIND_FILTERS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
            <select
              className={styles.select}
              value={filters.category}
              onChange={(e) => setFilters({ category: e.target.value as DecorationCategory | 'all' })}
              aria-label="Category"
            >
              <option value="all">All categories</option>
              {DECORATION_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <p className={styles.count}>
            {filtering ? `${shown.length} of ${accomplishments.length}` : `${accomplishments.length} accomplishments`}
            {filtering && (
              <>
                {' · '}
                <Button type="button" variant="ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
                  Clear filters
                </Button>
              </>
            )}
          </p>
        </div>
      )}

      {accomplishments.length > 0 && shown.length === 0 && (
        <p className={styles.empty}>Nothing matches these filters.</p>
      )}
      {shown.map((accomplishment) => (
        <AccomplishmentCard
          key={accomplishment.id}
          accomplishment={accomplishment}
          onClaim={(input) => claimMutation.mutateAsync({ id: accomplishment.id, input })}
          onWithdraw={(claimId) => withdrawMutation.mutateAsync(claimId)}
        />
      ))}
    </section>
  );
}
